import styled from 'styled-components';

import { FormBuilder } from '@components/form-builder';
import { defaultFormBuilderFields } from '@components/form-builder/form-builder';
import { FormV2 } from '@components/form/v2';
import { Spinner } from '@components/loading-spinner';
import { useOverlay } from '@components/overlay';
import { Stack } from '@components/stack';
import { Keys } from '@components/types/keyboard';
import { useKeyPress } from '@hooks/use-keypress';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  useCreateJourneyActionFormImage,
  useJourneyActionForm,
  useUpdateJourneyActionForm,
} from 'api/journey-action-forms/hooks';
import { useFormBuilderLists } from 'api/lists/hooks';

export const useEditJourneyActionFormOverlay = () => {
  return useOverlay(EditJourneyActionFormModal);
};

const EditJourneyActionFormModal = ({
  formId,
  openPreview,
  onClose,
}: {
  formId: string;
  openPreview?: boolean;
  onClose?: () => void;
}) => {
  const formQuery = useJourneyActionForm(formId);
  const { data: lists, isLoading: listsLoading } = useFormBuilderLists();
  const updateForm = useUpdateJourneyActionForm(
    formId,
    formQuery.data?.journeyActionId,
    formQuery.data?.journey?.shortId
  );
  const createFormImageQuery = useCreateJourneyActionFormImage();

  useKeyPress({
    key: Keys.Escape,
    enabled: true,
    handler: onClose ? () => onClose() : () => {},
  });

  if (formQuery.isLoading || listsLoading) {
    return <Spinner />;
  }

  if (!formQuery.isSuccess) {
    return <Spinner />;
  }

  const form = formQuery.data;

  return (
    <Container>
      <FormBuilder
        mode={openPreview ? 'preview' : 'edit'}
        fields={defaultFormBuilderFields}
        name={form.name}
        description={form.description || ''}
        form={form.fieldsConfig}
        submissionMessage={form.submissionMessageConfig}
        predefinedOptionsLists={lists}
        onSave={async (template, onSaveSuccess) => {
          const response = await updateForm.mutateAsync({
            ...template,
          });
          onSaveSuccess?.(
            response.fieldsConfig as FormV2,
            response.submissionMessageConfig as FormV2
          );
        }}
        onImageSave={async (props) => {
          const response = await createFormImageQuery.mutateAsync(props);
          return response.imageId;
        }}
        onComplete={onClose}
        useTabs
      />
    </Container>
  );
};

const Container = styled(Stack)({
  position: 'fixed',
  top: pxToRem(24),
  left: pxToRem(24),
  right: pxToRem(24),
  bottom: pxToRem(24),
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(16),
  boxShadow: theme.shadow.fancy,
  overflow: 'hidden',
});
